import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import PatientInformationScreen from './PatientInformationScreen/PatientInformationScreen';
import DeviceSelectionScreen from './DeviceSelectionScreen/DeviceSelectionScreen';
import CallEndedScreen from './CallEndedScreen/CallEndedScreen';
import IntroContainer from '../IntroContainer/IntroContainer';
import MediaErrorSnackbar from './MediaErrorSnackbar/MediaErrorSnackbar';
import RoomNameScreen from './RoomNameScreen/RoomNameScreen';
import OverlayLoading from '../OverlayLoading';
import { useAppState } from '../../state';
import { useParams } from 'react-router-dom';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { VIDEOBASEURL } from '../../constants';
import checkSpecialSitecode from '../../utils/checkSpecialSitecodes';
import { USER_DEFAULT_ROOM_KEY } from '../../constants';
import CallCanceledScreen from './CallCanceledScreen/CallCanceledScreen';
import * as Sentry from '@sentry/react';
import { postCodeRegex } from '../../constants/regex';
import { ASPEN_SITECODE } from '../../constants/sitecodes';
import cleanSitecodeString from '../../utils/cleanSitecodeString';

export enum Steps {
  roomNameStep,
  patientDetails,
  deviceSelectionStep,
  callEnded,
  callCanceled,
}

export enum SiteCodeValidStatus {
  notStarted,
  checkedAndPassed,
  checkedAndFailed,
}

const DEFAULT_POSTCODE = '9999';

export default function PreJoinScreens() {
  const { user } = useAppState();
  const { getAudioAndVideoTracks, room } = useVideoContext();
  const { URLRoomName } = useParams();
  const [step, setStep] = useState(Steps.roomNameStep);

  const [patientName, setPatientName] = useState<string>('');
  const [patientLastName, setPatientLastName] = useState<string>('');
  const [patientPhoneNumber, setPatientPhoneNumber] = useState<string>('');
  const [name, setName] = useState<string>(user?.displayName || '');
  const [category, setCategory] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [roomName, setRoomName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [disableSitecode, setDisableSitecode] = useState<boolean>(false);
  const [siteCode, setSiteCode] = useState<string>('');
  const [sitecodeValidStatus, setSitecodeValidStatus] = useState(SiteCodeValidStatus.notStarted);
  const [v2vCall, setV2vCall] = useState<boolean>(false);
  const [siteCodeError, setSiteCodeError] = useState<string>('');
  const [email, setEmail] = useState<string>(user?.displayName || '');
  const [phonenumber, setPhoneNumber] = useState<string>('');
  const [agentCall, setAgentCall] = useState<boolean>(false);
  const [bypassForm, setBypassForm] = useState<boolean>(false);
  const [haveTask, setHaveTask] = useState<string>('');
  const [isStatic, setIsStatic] = useState<boolean>(false);
  const [executionFlag, setExecutionFlag] = useState<boolean>(false);
  const [termsAndConditions, setTermsAndConditions] = useState<boolean>(false);
  const [mediaError, setMediaError] = useState<Error>();

  const handlePatientSubmit = useCallback((event: FormEvent<HTMLFormElement> | null) => {
    event && event.preventDefault();

    setStep(Steps.deviceSelectionStep);
  }, []);

  useEffect(() => {
    let rn = window.localStorage.getItem(USER_DEFAULT_ROOM_KEY);
    if (rn !== null) {
      setSiteCode(rn);
    }

    const searchStr = window.sessionStorage.getItem('searchStr');
    if (!!searchStr) {
      const searchParams = new URLSearchParams(searchStr);
      const agentName = searchParams.get('name');
      const agentRoom = searchParams.get('room');
      const task = searchParams.get('task');
      const isAgentCall = searchParams.get('isAgentCall');
      const isStaticParam = searchParams.get('isStatic');

      setHaveTask('');
      setIsStatic(false);
      // AGENT CALL
      if (isAgentCall === 'true' && !!agentRoom && !!agentName) {
        setAgentCall(true);
        setBypassForm(true);
        setSiteCode('');
        setRoomName(agentRoom);
        setName(agentName);
      }

      // TASK
      if (!!task && task === 'no') {
        setHaveTask('no');
      }

      // STATIC
      if (isStaticParam === 'true' && !!agentRoom) {
        let d = new Date();
        let theTime: string = d.getTime().toString();
        let uniqueCode: string = theTime.substr(theTime.length - 5);
        setIsStatic(true);
        setSiteCode(agentRoom);
        setRoomName(agentRoom);
        setName(agentRoom + '_' + uniqueCode);
        setBypassForm(true);
      }
    }
  }, [isStatic, setIsStatic]);

  const updateBrowserState = (code: string, newRoomName: string) => {
    window.localStorage.setItem(USER_DEFAULT_ROOM_KEY, code);

    if (!window.location.origin.includes('twil.io')) {
      window.history.replaceState(
        null,
        '',
        window.encodeURI(`${VIDEOBASEURL}/room/${newRoomName}${window.location.search || ''}`)
      );
    }
  };

  const checkSiteCode = async (code: string) => {
    let uri = window.location.href;
    let environment = 'prod';
    if (uri?.match('-uat')) environment = 'uat';
    if (uri?.match('-test')) environment = 'test';
    if (uri?.match('localhost')) environment = 'test';
    setLoading(true);
    fetch('https://9903qvvoj1.execute-api.ap-southeast-2.amazonaws.com/' + environment + '/issitevalid/' + code, {
      headers: { 'Content-Type': 'application/json', 'X-API-Key': 'QxzanvIyly6VKq7fH8gLx2NK1lBVAYyQ70qVlgjt' },
      method: 'GET',
    })
      .then(response => {
        return response.json();
      })
      .then(data => {
        if (data !== null && data.found) {
          let dd = new Date();
          let dtheTime: string = dd.getTime().toString();
          let duniqueCode: string = dtheTime.substr(dtheTime.length - 5);
          //isValid = true;
          const newRoomName = (code + '_' + duniqueCode).replace(' ', '').replace(/[^a-zA-Z0-9-_]/g, '');
          setRoomName(newRoomName);
          setStep(Steps.deviceSelectionStep);
          setSiteCodeError('');
          setSitecodeValidStatus(SiteCodeValidStatus.checkedAndPassed);
          updateBrowserState(code, newRoomName);
        } else {
          setSiteCodeError('Invalid post code or site code.');
          setSitecodeValidStatus(SiteCodeValidStatus.checkedAndFailed);
        }
        setLoading(false);
      })
      .catch(err => {
        setSiteCodeError('Invalid post code or site code.');
        setSitecodeValidStatus(SiteCodeValidStatus.checkedAndFailed);
        setLoading(false);
      });
  };

  const generateRoomName = (phone: string = '', firstname: string = '', lastname: string = '') => {
    const newRoomName = (!!phone ? `${firstname}_${phone}` : lastname).replace(' ', '').replace(/[^a-zA-Z0-9-_]/g, '');
    return newRoomName;
  };

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement> | null) => {
      event?.preventDefault();
      var newRoomName = '';

      if (agentCall) {
        //isValid = true;
        newRoomName = roomName.replace(' ', '').replace(/[^a-zA-Z0-9-_]/g, '');
        setStep(Steps.deviceSelectionStep);
      } else if (isStatic) {
        newRoomName = roomName.replace(' ', '').replace(/[^a-zA-Z0-9-_]/g, '');
        setStep(Steps.deviceSelectionStep);
      } else if (haveTask === 'no') {
        //isValid = true;
        newRoomName = (name + '_' + phonenumber).replace(' ', '').replace(/[^a-zA-Z0-9-_]/g, '');

        setSiteCode('');
        setRoomName(newRoomName);
        setStep(Steps.deviceSelectionStep);
      } else {
        const cleanedSitecode = cleanSitecodeString(siteCode);

        if (siteCode.match(postCodeRegex) === null) {
          // SITE CODE
          if (cleanedSitecode !== siteCode) {
            setSiteCode(cleanedSitecode);
          }
          checkSiteCode(cleanedSitecode);
        } else {
          // POST CODE
          newRoomName = generateRoomName(phonenumber, name, lastName);
          setRoomName(newRoomName);
          setStep(Steps.patientDetails);
          updateBrowserState(cleanedSitecode, newRoomName);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phonenumber, siteCode, name, lastName, haveTask, agentCall, isStatic]
  );

  useEffect(() => {
    const searchStr = window.sessionStorage.getItem('searchStr');

    if (URLRoomName && !searchStr) {
      setRoomName(URLRoomName);
      if (user?.displayName) {
        setStep(Steps.deviceSelectionStep);
      }
    }
    if (!!searchStr) {
      const searchParams = new URLSearchParams(searchStr);
      const isStaticParam = searchParams.get('isStatic');
      const isEscalate = searchParams.get('escalate') || '';
      const isName = decodeURIComponent(searchParams.get('name') || '');
      const isSitecode = decodeURIComponent(searchParams.get('sitecode') || '');
      const isLastName = decodeURIComponent(searchParams.get('lastname') || '');
      const isPostcode = decodeURIComponent(searchParams.get('postcode') || '');
      const isPhone = decodeURIComponent(searchParams.get('phone') || '');
      const isDisableSitecode = decodeURIComponent(searchParams.get('locked') || '');
      if (isDisableSitecode === 'yes') {
        setDisableSitecode(true);
      }
      const isEndSession = window.sessionStorage.getItem('endSession');

      const cleanedSitecode = cleanSitecodeString(isSitecode);
      const cleanedPostcode = cleanSitecodeString(isPostcode);
      if (!!isStaticParam && isStaticParam === 'true' && isEndSession === 'true') {
        /* TODO: Both the sessionStorage 'searchStr' and 'endSession' are playing a role as 
         controlling the status of the PrejoinScreen, sometimes they are conflicted. Better controllers
         should be implemented here
         */
        // window.sessionStorage.removeItem('searchStr');
        // window.sessionStorage.removeItem('endSession');
        setBypassForm(false);
        setStep(Steps.callEnded); //Thankyou, please come again screen
      }
      // voice to video link
      else if (isEscalate && isName && (cleanedSitecode || cleanedPostcode) && !executionFlag) {
        setV2vCall(true);
        setExecutionFlag(true);
        setName(isName);
        setLastName(isLastName);
        setPhoneNumber(isPhone);

        if (cleanedSitecode) {
          // Sitecode
          setSiteCode(cleanedSitecode);

          const isSpecial = checkSpecialSitecode(cleanedSitecode);
          if (isSpecial === ASPEN_SITECODE) {
            setSiteCode(ASPEN_SITECODE);
            checkSiteCode(ASPEN_SITECODE);
          } else {
            let newRoomName;
            if (isSpecial) {
              newRoomName = generateRoomName(isPhone, isName, isLastName);
              // special sitecode such as 'CALLBACK' and 'Followup'
              setSiteCode(cleanedPostcode || DEFAULT_POSTCODE);
              setSiteCodeError('');
              setSitecodeValidStatus(SiteCodeValidStatus.checkedAndPassed);
              updateBrowserState(cleanedSitecode, newRoomName);
              setRoomName(newRoomName);
            } else {
              checkSiteCode(cleanedSitecode);
            }
          }
        } else {
          // Postcode
          setSiteCode(cleanedPostcode);

          const newRoomName = generateRoomName(isPhone, isName, isLastName);
          setRoomName(newRoomName);
          updateBrowserState(cleanedPostcode, newRoomName);
          setStep(Steps.deviceSelectionStep);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, URLRoomName, step]);

  useEffect(() => {
    if (bypassForm) {
      handleSubmit(null);
      setBypassForm(false);
    }
    // eslint-disable-next-line
  }, [bypassForm]);

  useEffect(() => {
    if (step === Steps.deviceSelectionStep && !mediaError) {
      getAudioAndVideoTracks().catch(error => {
        console.dir(error);
        setMediaError(error);
      });
    }
  }, [getAudioAndVideoTracks, step, mediaError]);

  useEffect(() => {
    if (step === Steps.roomNameStep && room) {
      setTimeout(function() {
        Sentry.captureMessage(`RoomDisconnectEvent-${room.sid}`);
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (
      sitecodeValidStatus === SiteCodeValidStatus.checkedAndPassed ||
      (sitecodeValidStatus === SiteCodeValidStatus.checkedAndFailed && v2vCall === true)
    ) {
      if (sitecodeValidStatus === SiteCodeValidStatus.checkedAndFailed) {
        const searchStr = window.sessionStorage.getItem('searchStr');
        let isPostcode = '';
        let isPhone = '';
        let isName = '';
        let isLastname = '';
        if (!!searchStr) {
          const searchParams = new URLSearchParams(searchStr);
          isPostcode = decodeURIComponent(searchParams.get('postcode') || '');
          isName = decodeURIComponent(searchParams.get('name') || '');
          isLastname = decodeURIComponent(searchParams.get('lastname') || '');
          isPhone = decodeURIComponent(searchParams.get('phone') || '');
        }
        const cleanedPostcode = cleanSitecodeString(isPostcode);
        const newRoomName = generateRoomName(isPhone, isName, isLastname);
        setRoomName(newRoomName);
        updateBrowserState(cleanedPostcode || DEFAULT_POSTCODE, newRoomName);
        setSiteCode(cleanedPostcode || DEFAULT_POSTCODE);
        setSiteCodeError('');
      }
      setStep(Steps.deviceSelectionStep);
      setV2vCall(false);
    }
  }, [sitecodeValidStatus, v2vCall]);

  return (
    <IntroContainer>
      <MediaErrorSnackbar error={mediaError} />
      <OverlayLoading loading={loading} />
      {step === Steps.roomNameStep && (
        <RoomNameScreen
          name={name}
          roomName={roomName}
          email={email}
          phonenumber={phonenumber}
          disableSitecode={disableSitecode}
          lastName={lastName}
          termsAndConditions={termsAndConditions}
          siteCode={siteCode}
          siteCodeError={siteCodeError}
          setName={setName}
          category={category}
          setCategory={setCategory}
          setRoomName={setRoomName}
          setEmail={setEmail}
          setPhoneNumber={setPhoneNumber}
          setLastName={setLastName}
          setSiteCode={setSiteCode}
          setTermsAndConditions={setTermsAndConditions}
          handleSubmit={handleSubmit}
        />
      )}

      {step === Steps.patientDetails && (
        <PatientInformationScreen
          name={patientName}
          lastName={patientLastName}
          phonenumber={patientPhoneNumber}
          contactName={name}
          contactLastName={lastName}
          contactPhonenumber={phonenumber}
          setName={setPatientName}
          setLastName={setPatientLastName}
          setPhoneNumber={setPatientPhoneNumber}
          handleSubmit={handlePatientSubmit}
        />
      )}

      {step === Steps.deviceSelectionStep && (
        <DeviceSelectionScreen
          name={name}
          roomName={roomName}
          email={email}
          phonenumber={phonenumber}
          agentCall={agentCall}
          setStep={setStep}
          lastName={lastName}
          patientName={patientName}
          patientLastName={patientLastName}
          patientPhoneNumber={patientPhoneNumber}
          siteCode={siteCode}
          category={category}
          isStatic={isStatic}
        />
      )}

      {step === Steps.callEnded && <CallEndedScreen setStep={setStep} />}
      {step === Steps.callCanceled && <CallCanceledScreen />}
    </IntroContainer>
  );
}
